import "./slate-types.d.ts";
import {
  forwardRef,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Editable, withReact, Slate, ReactEditor } from "slate-react";
import { Transforms, createEditor, Editor, Element as SlateElement, Text } from "slate";
import { withHistory } from "slate-history";
import { Leaf } from "./components/Leaf";
import { Element } from "./components/Element";
import isHotkey from "is-hotkey";
import { withCorrectVoidBehavior } from "./utils/withCorrectVoidBehavior";
import { withPlugins } from "./utils/withPlugins";
import { deserialize } from "./utils/deserialize";
import { serialize } from "./utils/serialize";
import { isEmptyValue } from "./utils/isEmptyValue";
import { Toolbar } from "./Toolbar";
import {
  BlockButton,
  ClearFormatting,
  decreaseIndent,
  increaseIndent,
  IndentButton,
  isDisabledIndent,
  isDisabledOutdent,
  MarkButton,
  OutdentButton,
  toggleMark,
  ToolsDivider,
} from "./components/Buttons";
// import { HeadingSelect } from "./components/HeadingSelect";
import { LinkButtons } from "./components/LinkButtons";
import { Suggestions, MentionStore } from "./MentionStore";
import { observer } from "mobx-react";
import { MentionMenu } from "./components/Mention";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { FontSizeSelect } from "./components/FontSizeSelect";
import { TextColor } from "./components/TextColor";
import { FontFamilySelect } from "./components/FontFamilySelect";
import { ImageButton } from "./components/ImageButton";
import { TextBackground } from "./components/TextBackground";
import { useImageDrag } from "./utils/useImageDrag";

interface RichEditorProps {
  initialValue?: string;
  suggestions?: Suggestions;
  disabled?: boolean;
  withFontFamily?: boolean;
}

export interface RichEditorInstance {
  getHtml: () => string;
  setFromHtml: (html: string) => void;
  focus: () => void;
}

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
  "mod+s": "strikethrough",
};

// Helper function to replace spaces with &nbsp;
function convertSpacesToNbsp(htmlString: string = "") {
  // This regular expression looks for two or more spaces in a row
  // Replace it with &nbsp; followed by a regular space
  return htmlString.replace(/(?<=\S)\s{2,}/g, (match) => match.replace(/ /g, "&nbsp;"));
}

export const RichEditor = observer(
  forwardRef<RichEditorInstance, RichEditorProps>((initialProps, ref) => {
    const props: RichEditorProps = { withFontFamily: true, ...initialProps };
    const { t } = useTranslation(I18N_NS.RICH_EDITOR);
    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const editor = useMemo(() => withPlugins(withCorrectVoidBehavior(withHistory(withReact(createEditor())))), []);
    const [value, setValue] = useState([{ type: "paragraph" as const, children: [{ text: "" }] }]);
    const [fontSize, setFontSize] = useState<string>("Default");
    const { DropIndicator, handleDragOver, handleDragLeave, handleDrop } = useImageDrag(editor);

    const mentionStore = useMemo(() => new MentionStore(editor, props.suggestions), []);

    // Update suggestions when props.suggestions changes
    useEffect(() => {
      if (props.suggestions) {
        mentionStore.suggestions = props.suggestions;
      }
    }, [props.suggestions, mentionStore]);

    const setEmptyNode = useCallback(() => {
      const newNode = { type: "paragraph" as const, children: [{ text: " " }] };
      setValue([newNode]);
      Transforms.insertNodes(editor, newNode);
    }, [editor]);

    const getHtml = () => {
      return convertSpacesToNbsp(serialize(editor.children));
    };

    const handleChange = (values) => {
      if (!values.length) {
        setEmptyNode();
        return;
      }
      mentionStore.onEditorChange();
    };

    const setFromHtml = useCallback(
      (htmlString: string) => {
        if (htmlString) {
          const document = new DOMParser().parseFromString(htmlString, "text/html");
          const newValue = deserialize(document.body);
          // insert empty paragraph instead of empty text node because it breaks the editor
          if (newValue.length === 1 && typeof newValue[0].text === "string" && !newValue[0].type) {
            newValue[0] = { type: "paragraph", children: [{ text: "" }] };
          }
          // parse initial content from draft mail, remove empty text node
          // Wrap standalone text nodes in paragraph elements
          const filteredNewValue = newValue.reduce((acc, node, index, array) => {
            if (node.text) {
              // If previous node was a text and we already created a paragraph for it, add this node to that paragraph
              if (
                acc.length > 0 &&
                acc[acc.length - 1].type === "paragraph" &&
                array[index - 1] &&
                Object.prototype.hasOwnProperty.call(array[index - 1], "text")
              ) {
                acc[acc.length - 1].children.push(node);
              } else {
                if (node.text) {
                  // Start a new paragraph
                  acc.push({
                    type: "paragraph",
                    children: [node],
                  });
                }
              }
            } else {
              // Non-text node, add as is
              acc.push(node);
            }
            return acc;
          }, []);

          editor.children = filteredNewValue;
          setValue(filteredNewValue);
        } else {
          setEmptyNode();
        }
      },
      [editor]
    );

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (isHotkey("mod+a", event)) {
        event.preventDefault();
        Transforms.select(editor, []);
      }
      if (isHotkey("shift+tab", event)) {
        event.preventDefault();
        if (!isDisabledOutdent(editor)) {
          decreaseIndent(editor);
        }
      } else if (isHotkey("tab", event)) {
        event.preventDefault();
        if (!isDisabledIndent(editor)) {
          increaseIndent(editor);
        }
      } else if (isHotkey("shift+enter", event)) {
        event.preventDefault();
        Transforms.insertText(editor, "\n");
      } else if (event.key === "Enter" && !event.shiftKey) {
        // Check if we're in an empty list item
        const [listItemNode] = Array.from(
          Editor.nodes(editor, {
            match: (n) => SlateElement.isElement(n) && n.type === "li",
            mode: "lowest",
          })
        );

        if (listItemNode) {
          const [listItemElement] = listItemNode;

          // Check if the list item is empty (contains only an empty text node)
          const isEmptyListItem =
            listItemElement.children.length === 1 &&
            Text.isText(listItemElement.children[0]) &&
            listItemElement.children[0].text === "";

          if (isEmptyListItem) {
            event.preventDefault();
            decreaseIndent(editor);
            return;
          }
        }
      }
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
          return;
        }
      }
      mentionStore.onKeyDown(event);
    };

    const focus = useCallback(() => {
      ReactEditor.focus(editor);
    }, [editor]);

    useEffect(() => {
      if (isEmptyValue(value) && props.initialValue) {
        setFromHtml(props.initialValue);
      }
    }, [props.initialValue]);

    useImperativeHandle(
      ref,
      () => {
        return {
          setFromHtml: setFromHtml,
          getHtml: getHtml,
          focus: focus,
        };
      },
      []
    );

    return (
      <div className="slate-editor">
        <Slate editor={editor} initialValue={value} onValueChange={handleChange}>
          <Toolbar>
            {/* <HeadingSelect disabled={props.disabled} setFontSize={setFontSize} /> */}
            <FontSizeSelect disabled={props.disabled} value={fontSize} onChange={setFontSize} />
            <FontFamilySelect />
            <ToolsDivider />
            <MarkButton format="bold" icon="bold" title={t("Bold")} />
            <MarkButton format="italic" icon="italic" title={t("Italic")} />
            <MarkButton format="underline" icon="underline" title={t("Underline")} />
            <MarkButton format="strikethrough" icon="strikethrough" title={t("Strikethrough")} />
            <MarkButton format="code" icon="code" title={t("Code")} />
            <TextColor />
            <TextBackground />
            <ToolsDivider />
            <BlockButton format="quote" icon="quote-right" title={t("Quote")} />
            <BlockButton format="ol" icon="list-ol" title={t("Ordered list")} />
            <BlockButton format="ul" icon="list-ul" title={t("Bulleted list")} />
            <IndentButton />
            <OutdentButton />
            <BlockButton format="left" icon="align-left" title={t("Align left")} />
            <BlockButton format="center" icon="align-center" title={t("Align center")} />
            <BlockButton format="right" icon="align-right" title={t("Align right")} />
            <BlockButton format="justify" icon="align-justify" title={t("Justify")} />
            <LinkButtons />
            <ImageButton />
            <ToolsDivider />
            <ClearFormatting />
          </Toolbar>
          <Editable
            readOnly={props.disabled}
            className="slate--editable"
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            spellCheck
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
          {DropIndicator}
          <MentionMenu mentionStore={mentionStore} editor={editor} />
        </Slate>
      </div>
    );
  })
);

RichEditor.displayName = "RichEditor";
